import { TransactionDetailSectionProps } from '@/components/DataReflection/TransactionDetailSection/types';
import { useReferenceInvoiceTransactions } from '@/context/services/reportsType/invoiceReports/Edit.service';
import { ContentHeaderProps } from '@/features/InvoiceReport/Edit/components/UtilizePastDetails/TransactionDetailsDrawer/components/Contents/ContentHeader';
import { buildDetailFieldsFromTransactionProperties } from '@/features/InvoiceReport/Edit/components/UtilizePastDetails/TransactionDetailsDrawer/hooks/buildDetailFieldsFromTransactionProperties';
import { PROGRESS_STEP } from '@/features/InvoiceReport/Edit/components/UtilizePastDetails/constants';
import { useDataContext } from '@/features/InvoiceReport/Edit/components/UtilizePastDetails/context/UtilizePastDetailsDataContext';
import { useVisibleTransactionProperties } from '@/features/InvoiceReport/Edit/components/hooks/useVisibleTransactionProperties';
import {
  batchOptionsToNestedOptionMaps,
  useCustomTransactionPulldownLabels,
} from '@/hooks/useGetCustomTransactionPulldownLabels';
import { useTranslation } from '@/i18n';
import { isCommonError } from '@/libs/typeguard/isError';
import { InlineNotificationProps } from '@moneyforward/ap-frontend-components';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { useEffect, useMemo } from 'react';

export type UseTransactionDetailsDrawerOptions = {
  showColumnDetails?: boolean;
  canDisplayDrExcise?: boolean;
};

export function useTransactionDetailsDrawer(
  invoiceReportId?: string,
  options?: UseTransactionDetailsDrawerOptions
) {
  const { currentStep, setIsLoading, setNotificationDrawer, onSetCurrentStep } =
    useDataContext();
  const { data, isLoading, error } = useReferenceInvoiceTransactions(
    invoiceReportId ?? '',
    currentStep === PROGRESS_STEP.ON_SELECT_INVOICE
  );
  const { t } = useTranslation();
  const isCustomTransactionFeatureEnabled = useBooleanFlagValue(
    'custom-transaction-two',
    false
  );

  const { showColumnDetails = true, canDisplayDrExcise = true } = options ?? {};

  const visibleTransactionProperties = useVisibleTransactionProperties({
    canDisplayTransactionDetail: showColumnDetails,
    canDisplayDrExcise,
  });

  const { data: pulldownOptions, isLoading: isLoadingPulldownOptions } =
    useCustomTransactionPulldownLabels(
      data?.data?.invoice_transactions ?? [],
      visibleTransactionProperties
    );

  const pulldownOptionMap = useMemo(
    () => batchOptionsToNestedOptionMaps(pulldownOptions ?? []),
    [pulldownOptions]
  );

  const resolvePulldownDisplayName = useMemo(
    () => (propertyId: string, storedValue: string) =>
      pulldownOptionMap.get(propertyId)?.get(storedValue)?.label ?? '',
    [pulldownOptionMap]
  );

  const detailFields = useMemo(
    () =>
      buildDetailFieldsFromTransactionProperties({
        transactionProperties: visibleTransactionProperties,
        t,
        isCustomTransactionFeatureEnabled,
        resolvePulldownDisplayName,
      }),
    [
      visibleTransactionProperties,
      t,
      isCustomTransactionFeatureEnabled,
      resolvePulldownDisplayName,
    ]
  );

  const { detailProps, contentDetailProps } = useMemo(() => {
    const res = data?.data;
    let detailProps: TransactionDetailSectionProps | undefined;
    let contentDetailProps: ContentHeaderProps | undefined;
    if (res) {
      detailProps = {
        isSelected: true,
        transactionList: res?.invoice_transactions,
        fields: detailFields,
        warningMessage: {
          enabled: true,
          message:
            '作成済みの明細は破棄され、選択している申請の明細が反映されます。',
        },
      };
      contentDetailProps = {
        content: {
          href: res?.detail_invoice_report_path ?? '',
          textLink: res?.number_without_prefix,
          title: res?.title,
        },
      };
    }
    return { detailProps, contentDetailProps };
  }, [data, detailFields]);

  const notification: InlineNotificationProps = useMemo(
    () => ({
      visible: isCommonError(error ?? {}),
      type: 'error',
      message:
        isCommonError(error ?? {}) && Array.isArray(error?.messages)
          ? error?.messages.join('')
          : String(error?.messages) ?? t('system_error'),
    }),
    [error, t]
  );
  // Execute side effects declaratively
  useEffect(() => {
    // Set loading state
    setIsLoading(isLoading);

    // Handle step transitions
    if (currentStep === PROGRESS_STEP.ON_SELECT_INVOICE) {
      if (detailProps && !error) {
        onSetCurrentStep(PROGRESS_STEP.ON_OPEN_DRAWER);
      } else if (error && notification.visible) {
        setNotificationDrawer(notification);
        onSetCurrentStep(PROGRESS_STEP.ON_OPEN_DIALOG);
      } else {
        setNotificationDrawer(null);
      }
    }
  }, [
    isLoading,
    notification,
    detailProps,
    currentStep,
    setIsLoading,
    error,
    setNotificationDrawer,
    onSetCurrentStep,
  ]);

  return {
    detailProps,
    isLoading,
    contentDetailProps,
    notification,
    pulldownOptionMap,
    isLoadingPulldownOptions,
  };
}
